import React, {useEffect, useRef, useState} from 'react';
import './CFAdminVehicleFootnotes.scss'
import {Formik} from "formik";
import * as Yup from "yup";
import {FVPSelect, FVPTextInputWithSize} from "../../components/formField";
import CFAdminNotification from "../../components/CFAdminNotification";
import VehicleConfigService from "../../services/VehicleConfigService";
import FootnotesService from "../../services/FootnotesService";
import {FootnotesProps} from "./types";
import Stepper from "react-stepper-horizontal";
import CFAdminDataTable from "../../components/CFAdminDataTable";
import {VehicleAttributeType} from "../CFAdminVehicleConfig/types";
import {OperationLanguage} from "../CFAdminOperations/types";
import {CountryLanguageEnum} from "../../constants/CountryLanguageEnum"

type OptionType = { description: string; code?: string };

const AttributeHeader = ({ attributeDescription }: { attributeDescription: string }) => (
    <div className="flex">
        <div className="inline-flex">
            {attributeDescription}
        </div>
    </div>
);

const VehicleSelectCell = (props: any) => {
    const { column } = props;
    return (
        <div>
            <button data-testid={`vc-step-next${props.row.index}`}
                    type="button"
                    onClick={() => column.handleVehicleClickFN(props)}>
                Select
            </button>
        </div>
    );
};

const schema = Yup.object().shape({
    make: Yup.string()
        .required("Make is a required field"),
    model: Yup.string()
        .required("Model is a required field"),
    year: Yup.string()
        .required("Year is a required field"),
});

const createAttributeHeaderComponent = (attributeDescription: string) => {
    const HeaderColumn = () => <AttributeHeader attributeDescription={attributeDescription} />;
    return HeaderColumn;
};

function CFAdminVehicleFootnotesFN() {
    const childCompRefFN = useRef<any>();
    const [activeStepFN, setActiveStepFN] = useState(0);
    const [nextStepFN, setNextStepFN] = useState(0);
    const [makesOptionsFN, setMakesOptionsFN] = useState<OptionType[]>([]);
    const [modelsOptionsFN, setModelsOptionsFN] = useState<OptionType[]>([]);
    const [yearsOptionsFN, setYearsOptionsFN] = useState<OptionType[]>([]);
    const [isOpenFN, setIsOpenFN] = useState(false);
    const [vinFN, setVinFN] = useState('');
    const [vehicleDetailsFN, setVehicleDetailsFN] = useState<FootnotesProps>({
        make: '',
        model: '',
        year: '',
        vin: '',
        modelYearId: '',
        countryLanguageId: 1
    });
    const [vinDetailsFN, setVinDetailsFN] = useState<any>(null);
    const [makeSelectValueFN, setMakeSelectValueFN] = useState('');
    const [modelSelectValueFN, setModelSelectValueFN] = useState('');
    const [yearSelectValueFN, setYearSelectValueFN] = useState('');
    const [isVinDecodedFN, setIsVinDecodedFN] = useState(false);
    const [isVinInvalidFN, setIsVinInvalidFN] = useState(false);
    const [operationsFN, setOperationsFN] = useState<{ id: number; description: string }[]>([]);
    const [footnotesFN, setFootnotesFN] = useState<{ id: number; description: string }[]>([]);
    const EMPTY_FOOTNOTE_VALUE = '';
    const [selectedFootnotesFN, setSelectedFootnotesFN] = useState<{ [key: number]: number | null }>({});
    const [footnoteMappingMetaFN, setFootnoteMappingMetaFN] = useState<{ [key: number]: number | null }>({});
    const [columnValues, setColumnValues] = useState<any>([]);
    const [dataValues, setDataValues] = useState<any>([]);
    const [selectedVehicleIdFN, setSelectedVehicleIdFN] = useState<number | null>(null);

    const getOptionDescriptionFN = (options: OptionType[], code: string) => {
        const option = options.find(item => String(item.code) === String(code));
        return option?.description ?? String(code ?? '');
    };

    const hasSelectedVehicleFN = Boolean(vehicleDetailsFN.make && vehicleDetailsFN.model && vehicleDetailsFN.year);
    const selectedMakeFN = hasSelectedVehicleFN ? getOptionDescriptionFN(makesOptionsFN, vehicleDetailsFN.make) : '';
    const selectedYearFN = hasSelectedVehicleFN ? getOptionDescriptionFN(yearsOptionsFN, vehicleDetailsFN.year) : '';
    const selectedModelFN = hasSelectedVehicleFN ? getOptionDescriptionFN(modelsOptionsFN, vehicleDetailsFN.model) : '';
    const stepsFN = [
        {title: 'Select Model', onClick: () => setActiveStepFN(0)},
        {title: 'Select Vehicle', onClick: () => setActiveStepFN(1)},
        {title: 'Operation and Footnotes Configurations', onClick: () => setActiveStepFN(2)},
        {title: 'Review Configurations', onClick: () => setActiveStepFN(3)},
    ];

    const handleFootnoteChangeFN = (operationId: number, footnoteIdValue: string) => {
        const parsedFootnoteId = footnoteIdValue === EMPTY_FOOTNOTE_VALUE ? null : Number(footnoteIdValue);
        const hasMeta = Object.hasOwn(footnoteMappingMetaFN, operationId);
        const metaValue = hasMeta ? footnoteMappingMetaFN[operationId] : undefined;
        const isClearingSelection = parsedFootnoteId === null || Number.isNaN(parsedFootnoteId);

        setSelectedFootnotesFN(prev => {
            if (isClearingSelection) {
                if (hasMeta && metaValue !== null && metaValue !== undefined) {
                    return {
                        ...prev,
                        [operationId]: null
                    };
                }
                const updatedSelections = {...prev};
                delete updatedSelections[operationId];
                return updatedSelections;
            }
            return {
                ...prev,
                [operationId]: parsedFootnoteId
            };
        });

        setFootnoteMappingMetaFN(prev => {
            if (isClearingSelection) {
                if (Object.hasOwn(prev, operationId) && prev[operationId] === null) {
                    const updatedMeta = {...prev};
                    delete updatedMeta[operationId];
                    return updatedMeta;
                }
                return prev;
            }
            if (Object.hasOwn(prev, operationId)) {
                return prev;
            }
            return {
                ...prev,
                [operationId]: null
            };
        });
    };

    const getVehicleMakesFN = async () => {
        VehicleConfigService.getVehicleMakes()
            .then(responseFN1 => {
                const responseModelFN1 = responseFN1.data?.response;
                let makeOptionsFN: OptionType[] = [];
                if (responseModelFN1) {
                    responseModelFN1.forEach((element: any) => {
                        let data: OptionType = {
                            description: element.description,
                            code: element.id
                        };
                        makeOptionsFN.push(data);
                    });
                    setMakesOptionsFN(makeOptionsFN);
                }
            })
            .catch(e => {
                console.log(e);
            });
    };

    const getVehicleModelsFN = async (makeId: any, yearId: any) => {
        const selectedMakeOptionFN = makesOptionsFN.find(option => String(option.code) === makeId);
        const selectedYearOptionFN = yearsOptionsFN.find(option => String(option.code) === yearId);
        VehicleConfigService.getVehicleModels(selectedMakeOptionFN?.description ?? '', selectedYearOptionFN?.description ?? '')
            .then(responseFN2 => {
                const responseModelFN2 = responseFN2.data?.response;
                let modelOptionsFN: OptionType[] = [];
                if (responseModelFN2) {
                    responseModelFN2.forEach((element: any) => {
                        let data: OptionType = {
                            description: element.description,
                            code: element.id
                        };
                        modelOptionsFN.push(data);
                    });
                    setModelsOptionsFN(modelOptionsFN);
                }
            })
            .catch(e => {
                console.log(e);
            });
    };

    const getVehicleYearsFN = async () => {
        VehicleConfigService.getVehicleYears()
            .then(responseFN3 => {
                const responseModelFN3 = responseFN3.data?.response;
                let yearOptionsFN: OptionType[] = [];
                if (responseModelFN3) {
                    responseModelFN3.forEach((element: any) => {
                        let data: OptionType = {
                            description: element.description,
                            code: element.id
                        };
                        yearOptionsFN.push(data);
                    });
                    setYearsOptionsFN(yearOptionsFN);
                }
            })
            .catch(e => {
                console.log(e);
            });
    };
    const isUSLanguage = (lang: { countryLanguageId: number }) => lang.countryLanguageId === CountryLanguageEnum.US_ENG;
    const getOperationsFN = async (vehicleId?: number): Promise<{ id: number; description: string }[]> => {
        if (vehicleId === undefined || vehicleId === null) {
            setOperationsFN([]);
            return [];
        }
        try {
            const response = await VehicleConfigService.getAllOperationByVehicle(vehicleId);
            const operationList = response.data?.operationList ?? [];
            const mappedOperations = operationList.reduce((acc: {
                id: number;
                description: string
            }[], operation: any) => {
                const usLanguage: OperationLanguage | undefined = operation.operationLanguageList?.find(isUSLanguage);
                if (usLanguage) {
                    acc.push({
                        id: operation.operationId,
                        description: operation.operationCode+"-"+usLanguage.description
                    });
                }
                return acc;
            }, []);
            setOperationsFN(mappedOperations);
            return mappedOperations;
        } catch (e) {
            console.log(e);
            setOperationsFN([]);
            return [];
        }
    };

    const loadExistingFootnoteMappingsFN = async (vehicleId: number, operationsForVehicle: {
        id: number;
        description: string
    }[] = operationsFN) => {
        try {
            const response = await VehicleConfigService.getExistVehicleOperationFootNotes(vehicleId);
            const mappingList = response?.data?.footNotesMappingList ?? [];
            const validOperationIds = new Set(operationsForVehicle.map(operation => operation.id));
            const mappedSelections = Array.isArray(mappingList)
                ? mappingList.reduce((acc: { [key: number]: number | null }, mapping: any) => {
                    const operationId = Number(mapping?.operationId);
                    const footNotesId = Number(mapping?.footNotesId);
                    if (!Number.isFinite(operationId) || !Number.isFinite(footNotesId) || !validOperationIds.has(operationId)) {
                        return acc;
                    }
                    acc[operationId] = footNotesId;
                    return acc;
                }, {})
                : {};
            const mappedMeta = Array.isArray(mappingList)
                ? mappingList.reduce((acc: { [key: number]: number | null }, mapping: any) => {
                    const operationId = Number(mapping?.operationId);
                    const vehicleOperFootNotesId = Number(mapping?.vehicleOperFootNotesId);
                    if (!Number.isFinite(operationId) || !validOperationIds.has(operationId)) {
                        return acc;
                    }
                    acc[operationId] = Number.isFinite(vehicleOperFootNotesId) ? vehicleOperFootNotesId : null;
                    return acc;
                }, {})
                : {};
            setSelectedFootnotesFN(mappedSelections);
            setFootnoteMappingMetaFN(mappedMeta);
        } catch (e) {
            console.log(e);
            setSelectedFootnotesFN({});
            setFootnoteMappingMetaFN({});
        }
    };

    const getAllFootnotesFN = async () => {
        try {
            const response = await FootnotesService.getAll();
            const footNotesList = response?.data?.footNotesList ?? [];
            const mappedFootnotes = Array.isArray(footNotesList)
                ? footNotesList.reduce((acc: { id: number; description: string }[], footnote: any) => {
                    const footnoteId = Number(footnote?.footNotesId);
                    const footnoteCode = footnote?.footNotesCode;
                    if (!Number.isFinite(footnoteId)) {
                        return acc;
                    }
                    const usLanguage = footnote?.footNotesLanguageList?.find(isUSLanguage);
                    if (usLanguage?.description) {
                        acc.push({
                            id: footnoteId,
                            description: footnoteCode+"-"+usLanguage.description
                        });
                    }
                    return acc;
                }, [])
                : [];
            setFootnotesFN(mappedFootnotes);
        } catch (e) {
            console.log(e);
            setFootnotesFN([]);
        }
    };

    const onChangeMakeFN = async (e: any) => {
        vehicleDetailsFN.make = e.target.value;
        setMakeSelectValueFN(e.target.value);
        if (vehicleDetailsFN.make !== '' && vehicleDetailsFN.year !== '') {
            setModelsOptionsFN([]);
            getVehicleModelsFN(vehicleDetailsFN.make, vehicleDetailsFN.year);
        }
    };

    const onChangeYearFN = async (e: any) => {
        vehicleDetailsFN.year = e.target.value;
        setYearSelectValueFN(e.target.value);
        if (vehicleDetailsFN.make !== '' && vehicleDetailsFN.year !== '') {
            setModelsOptionsFN([]);
            getVehicleModelsFN(vehicleDetailsFN.make, vehicleDetailsFN.year);
        }
    };

    const onChangeModelFN = async (e: any) => {
        vehicleDetailsFN.model = e.target.value;
        setModelSelectValueFN(e.target.value);
    };

    useEffect(() => {
        getSectionComponentFN();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStepFN, nextStepFN]);

    function getSectionComponentFN() {
        switch (activeStepFN) {
            case 0:
                getAllFootnotesFN();
                getVehicleMakesFN();
                getVehicleYearsFN();
                if (nextStepFN === 1) {
                    handleStepperNextFN(1);
                }
                break;
            case 1:
                getVehicleConfigurationsFN();
                if (nextStepFN === 2) {
                    handleStepperNextFN(2);
                } else if (nextStepFN === 0) {
                    handleStepperNextFN(0);
                }
                break;
            case 2:
                if (nextStepFN === 3) {
                    handleStepperNextFN(3);
                } else if (nextStepFN === 1) {
                    handleStepperNextFN(1);
                }
                break;
            case 3:
                if (nextStepFN === 2) {
                    handleStepperNextFN(2);
                }
                break;
            default:
                console.error("Unrecognized step");
        }
    }

    const confirmSuccessMessageFN = async (value: string) => {
        childCompRefFN.current.successMessage(value);
    };

    const submitVehicleFootnotesConfigFN = async () => {
        if (selectedVehicleIdFN === null) {
            console.warn("No vehicle selected for footnote submission");
            return;
        }
        const footNotesMapping = operationsFN.reduce((acc: {
            vehicleOperFootNotesId: number | null;
            operationId: number;
            footNotesId: number | null;
        }[], operation) => {
            const hasSelectedValue = Object.hasOwn(selectedFootnotesFN, operation.id);
            const hasMetaValue = Object.hasOwn(footnoteMappingMetaFN, operation.id);
            if (!hasSelectedValue && !hasMetaValue) {
                return acc;
            }
            const selectedValue = hasSelectedValue ? selectedFootnotesFN[operation.id] : null;
            const footNotesId = selectedValue ?? null;
            const vehicleOperFootNotesId = hasMetaValue ? footnoteMappingMetaFN[operation.id] ?? null : null;
            acc.push({
                vehicleOperFootNotesId,
                operationId: operation.id,
                footNotesId
            });
            return acc;
        }, []);
        const requestBody = {
            footNotesMapping
        };
        try {
            await VehicleConfigService.saveFootnoteMappings(selectedVehicleIdFN, requestBody);
            confirmSuccessMessageFN("Vehicle footnote mappings saved successfully.");
            resetFormFN();
        } catch (error) {
            console.error(error);
            confirmErrorMessageFN("Failed to save vehicle footnote mappings. Please try again.");
        }
    }

    const processResponseModelFN = (columns: any, parentElement: any) => {
        parentElement.vehicleAttributes.forEach((childElement: VehicleAttributeType) => {
            pushParentColumnsFN(childElement, columns);
        });
    }

    const pushParentColumnsFN = (childElement: VehicleAttributeType, columns: any) => {
        columns.push({
            Header: createAttributeHeaderComponent(childElement.attributeDescription),
            disableSortBy: true,
            accessor: 'attributeValueDescription' + childElement.attributeId
        })
    }

    const pushColumnsFN = (columns: any) => {
        columns.push({
            Header: "Actions", disableSortBy: true, accessor: '',
            Cell: VehicleSelectCell,
            handleVehicleClickFN: handleVehicleClickFN
        });
    }

    const handleVehicleClickFN = async (event: any) => {
        const rawVehicleId = event?.row?.original?.vehicleId;
        const vehicleId = Number(rawVehicleId);
        if (!Number.isFinite(vehicleId)) {
            return;
        }
        setSelectedVehicleIdFN(vehicleId);
        setSelectedFootnotesFN({});
        setFootnoteMappingMetaFN({});
        const operationsForVehicle = await getOperationsFN(vehicleId);
        await loadExistingFootnoteMappingsFN(vehicleId, operationsForVehicle);
        setNextStepFN(activeStepFN + 1);
    };

    function createJsonFN(count: number, vehicleId: number, childElements: any) {
        let jsonData: any = {};
        childElements.forEach((childElement: VehicleAttributeType) => {
            let columnName = 'attributeValueDescription' + childElement.attributeId;
            jsonData[columnName] = childElement.attributeValueDescription;
        });
        jsonData['id'] = 'Configuration ' + count;
        jsonData['vehicleId'] = vehicleId;

        return jsonData;
    }

    function getVehicleConfigurationsFN() {
        const selectedMake = makeSelectValueFN;
        const selectedModel = modelSelectValueFN;
        const selectedYear = yearSelectValueFN;

        const selectedMakeOptionFN = makesOptionsFN.find(option => String(option.code) === selectedMake);
        const selectedModelOptionFN = modelsOptionsFN.find(option => String(option.code) === selectedModel);
        const selectedYearOptionFN = yearsOptionsFN.find(option => String(option.code) === selectedYear);
        VehicleConfigService.getVehicleConfigurationDetails(selectedMakeOptionFN?.description ?? '', selectedModelOptionFN?.description ?? '', selectedYearOptionFN?.description ?? '')
            .then(responseFN => {

                const responseModelFN = responseFN.data?.vehicles;
                if (responseModelFN) {

                    let vehColumns: any = [];
                    let vehData: any = [];
                    let vehCount: number = 0;
                    let isColAdded: boolean = false;
                    responseModelFN.forEach((parentElement: any) => {
                        vehCount = vehCount + 1;
                        if (!isColAdded) {
                            vehColumns.push({Header: '', accessor: 'id', disableSortBy: true});
                            processResponseModelFN(vehColumns, parentElement);
                            pushColumnsFN(vehColumns);
                            isColAdded = true;
                        }
                        vehData.push(createJsonFN(vehCount, parentElement.vehicleId, parentElement.vehicleAttributes));
                    });
                    setColumnValues(vehColumns);
                    setDataValues(vehData);
                }
            })
            .catch(e => {
                console.log(e);
            });
    }

    const getDecodeVinFN = async () => {
        VehicleConfigService.getDecodeVin(vehicleDetailsFN.vin)
            .then(responseFN4 => {
                const decodeVinDataFN = responseFN4.data;
                if (decodeVinDataFN) {
                    setVinDetailsFN(decodeVinDataFN);
                    setMakeSelectValueFN(decodeVinDataFN.makeId);
                    setYearSelectValueFN(decodeVinDataFN.yearId);
                    setVehicleDetailsFN({
                        ...vehicleDetailsFN,
                        make: String(decodeVinDataFN.makeId),
                        model: String(decodeVinDataFN.modelId),
                        year: String(decodeVinDataFN.yearId),
                    });
                    setModelsOptionsFN([]);
                    getVehicleModelsFN(String(decodeVinDataFN.makeId), String(decodeVinDataFN.yearId));
                    setModelSelectValueFN(decodeVinDataFN.modelId);
                    setIsVinDecodedFN(true);
                }
            })
            .catch(e => {
                setIsVinInvalidFN(true);
                confirmErrorMessageFN("Please enter Valid VIN");
                console.error(e);
            });
    };

    const handleSubmitFN = (values: any) => {
        toggleModalFN();
    };

    const decodeVinFN = () => {
        if (vehicleDetailsFN.vin === '') {
            confirmErrorMessageFN("Please enter VIN value");
            return;
        }
        getDecodeVinFN();
    }

    const resetFormFN = () => {
        setVinFN('');
        setVehicleDetailsFN({
            make: '',
            model: '',
            year: '',
            vin: '',
            modelYearId: '',
            countryLanguageId: 1
        });
        setVinDetailsFN(null);
        setMakeSelectValueFN('');
        setYearSelectValueFN('');
        setModelsOptionsFN([]);
        setModelSelectValueFN('');
        setActiveStepFN(0);
        setNextStepFN(0);
        setSelectedVehicleIdFN(null);
        setSelectedFootnotesFN({});
        setFootnoteMappingMetaFN({});
    };

    const toggleModalFN = () => {
        setIsOpenFN(!isOpenFN);
    }

    const confirmErrorMessageFN = async (value: any) => {
        childCompRefFN.current.failureMessage(value);
    };

    const onChangeVinFN = async (e: any) => {
        vehicleDetailsFN.vin = e.target.value;
        setVinFN(e.target.value);
        setIsVinInvalidFN(false);
        setIsVinDecodedFN(false);
    };

    const handleStepperNextFN = (nextStepFNVal: number) => {
        setActiveStepFN(nextStepFNVal);
    };
    const handleNextClickFN = (values: any) => {
        if (activeStepFN === 0) {
            if (isVinInvalidFN) {
                confirmErrorMessageFN("Please enter valid VIN to proceed further");
                return;
            }
            if (!isVinDecodedFN && vinFN !== '') {
                confirmErrorMessageFN("Please click on Decode button to proceed further");
                return;
            }
            if ((vehicleDetailsFN.make === '' || vehicleDetailsFN.year === '' || vehicleDetailsFN.model === '')
                && (vinDetailsFN === undefined || vinDetailsFN === null)) {
                confirmErrorMessageFN("Please Enter VIN or Select Make, Year and Model.");
                return;
            }
        }
        setNextStepFN(activeStepFN + 1);
    };

    const handlePreviousClickFN = () => {
        setNextStepFN(activeStepFN - 1);
    };

    return (
            <div className="config-component">
                <h2 className="fomo-type--title">
                    Manage Footnotes Recommendations
                </h2>
                <Formik
                    validationSchema={schema}
                    initialValues={{make: '', modelDesc: ''}}
                    onSubmit={handleSubmitFN}
                >
                    {({values, errors, touched, handleChange, handleBlur, handleSubmit, isValid, dirty}) => (
                        <div className="fvp-form">
                            <div className="modal-top-div">
                                <div className="modal-body">
                                    <div className="stepper">
                                        <Stepper steps={stepsFN} activeStep={activeStepFN}/>
                                        <div className="active-step">
                                                {activeStepFN === 0 &&
                                                    <div className="ext1">
                                                            Please enter a VIN for Decode vehicle
                                                            details <b>OR</b> Select Make, Year and Model.
                                                                <div className="container">
                                                                    <div className="box">
                                                                        <div className="box-row">
                                                                            <div className="box-cell box1">
                                                                                <div className="fvp-form__subgroup">
                                                                                    <FVPTextInputWithSize
                                                                                        style={{width: "200px"}}
                                                                                        size={17}
                                                                                        label="Vin:"
                                                                                        name="vinFN"
                                                                                        data-testid="vinFN"
                                                                                        value={vinFN}
                                                                                        onChange={onChangeVinFN}/>
                                                                                </div>
                                                                                <div className="fvp-form__subgroup">
                                                                                    <button data-testid="step-decodeFN"
                                                                                            className="fvp-button button-right-align"
                                                                                            type="submit"
                                                                                            onClick={decodeVinFN}>
                                                                                        Decode
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                            <div className="box-cell box1">
                                                                                <div className="fvp-form__subgroup">
                                                                                    &nbsp;&nbsp;&nbsp;
                                                                                </div>
                                                                                <div className="fvp-form__subgroup">
                                                                                    &nbsp;&nbsp;&nbsp;
                                                                                </div>
                                                                                <div className="fvp-form__subgroup">
                                                                                    <center>
                                                                                        <b className="box-cell box3">OR</b>
                                                                                    </center>
                                                                                </div>
                                                                            </div>
                                                                            <div className="box-cell box2">
                                                                                <div className="fvp-form__subgroup">
                                                                                    <FVPSelect
                                                                                        style={{width: "200px"}}
                                                                                        label="Make:"
                                                                                        name="makesOptionsFN"
                                                                                        data-testid="makeFN"
                                                                                        value={makeSelectValueFN}
                                                                                        onChange={onChangeMakeFN}
                                                                                        options={makesOptionsFN}
                                                                                        required
                                                                                    />
                                                                                </div>
                                                                                <div className="fvp-form__subgroup">
                                                                                    <FVPSelect
                                                                                        style={{width: "200px"}}
                                                                                        label="Year:"
                                                                                        name="yearsOptionsFN"
                                                                                        value={yearSelectValueFN}
                                                                                        data-testid="yearFN"
                                                                                        onChange={onChangeYearFN}
                                                                                        options={yearsOptionsFN}
                                                                                        required
                                                                                    />
                                                                                </div>
                                                                                <div className="fvp-form__subgroup">
                                                                                    <FVPSelect
                                                                                        style={{width: "200px"}}
                                                                                        label="Model:"
                                                                                        name="modelsOptionsFN"
                                                                                        data-testid="modelFN"
                                                                                        value={modelSelectValueFN}
                                                                                        onChange={onChangeModelFN}
                                                                                        options={modelsOptionsFN}
                                                                                        required
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                        </div>
                                                }
                                                {activeStepFN === 1 && (
                                                        <div className="ext1">
                                                            {hasSelectedVehicleFN && (
                                                                <div className="vehicle-summary">
                                                                    <strong>Make :</strong> {selectedMakeFN}, <strong>Model :</strong> {selectedModelFN}, <strong>Year :</strong> {selectedYearFN}
                                                                </div>
                                                            )}
                                                            Please Select Vehicles.
                                                            <br/>
                                                            <CFAdminDataTable
                                                                columns={columnValues}
                                                                data={dataValues}
                                                                textModal={''}
                                                                showFilter={false}
                                                                showAdd={false}
                                                                showPagination={false}
                                                                pageSize={1000}
                                                            />
                                                        </div>
                                                )}
                                                {activeStepFN === 2 && (
                                                        <div className="ext1">
                                                            {hasSelectedVehicleFN && (
                                                                <div className="vehicle-summary">
                                                                    <strong>Make :</strong> {selectedMakeFN}, <strong>Model :</strong> {selectedModelFN}, <strong>Year :</strong> {selectedYearFN}
                                                                </div>
                                                            )}
                                                            Please Configure Operations and Footnotes from the below
                                                            options.<br/>
                                                            * One to One mapping between Operation and Footnote is
                                                            required.
                                                            <br/>
                                                            {operationsFN.length > 0 && (
                                                                <>
                                                                    <table className="fixed-table-header">
                                                                        <thead>
                                                                        <tr>
                                                                            <th>Operation</th>
                                                                            <th>Footnote</th>
                                                                        </tr>
                                                                        </thead>
                                                                    </table>
                                                                    <div className="scrollable-table-container">
                                                                        <table className="scrollable-table-body">
                                                                            <tbody>
                                                                            {operationsFN.map(opFN => (
                                                                                <tr key={opFN.id}>
                                                                                    <td>{opFN.description}</td>
                                                                                    <td>
                                                                                        <select
                                                                                            value={Object.hasOwn(selectedFootnotesFN, opFN.id) && selectedFootnotesFN[opFN.id] !== null && selectedFootnotesFN[opFN.id] !== undefined
                                                                                                ? String(selectedFootnotesFN[opFN.id])
                                                                                                : EMPTY_FOOTNOTE_VALUE}
                                                                                            onChange={e => handleFootnoteChangeFN(opFN.id, e.target.value)}
                                                                                        >
                                                                                            <option
                                                                                                value={EMPTY_FOOTNOTE_VALUE}>Select
                                                                                                Footnote
                                                                                            </option>
                                                                                            {footnotesFN.map(fnFN => (
                                                                                                <option key={fnFN.id}
                                                                                                        value={String(fnFN.id)}>{fnFN.description}</option>
                                                                                            ))}
                                                                                        </select>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                )}
                                                {activeStepFN === 3 && (
                                                        <div className="ext1">
                                                            {hasSelectedVehicleFN && (
                                                                <div className="vehicle-summary">
                                                                    <strong>Make :</strong> {selectedMakeFN}, <strong>Model :</strong> {selectedModelFN}, <strong>Year :</strong> {selectedYearFN}
                                                                </div>
                                                            )}
                                                            Please review Operations and Footnotes configuration.
                                                            <div className="scrollable-table-container">
                                                                <ul>
                                                                    {operationsFN.filter(opFN => {
                                                                        const original = footnoteMappingMetaFN[opFN.id];
                                                                        const current = selectedFootnotesFN[opFN.id];
                                                                        return original !== current;
                                                                    }).map(opFN => {
                                                                        const selectedFnIdFN = selectedFootnotesFN[opFN.id];
                                                                        const selectedFnFN = footnotesFN.find(fnFN => fnFN.id === selectedFnIdFN);
                                                                        return (
                                                                            <li key={opFN.id}>
                                                                                {opFN.description} &mdash; {selectedFnFN ? selectedFnFN.description : 'No footnote selected'}
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                )}
                                        </div>
                                        <div className="modal-footer button-frame">
                                            <button data-testid="step-cancelFN"
                                                    className="fvp-button--outline fvp-button"
                                                    type="submit"
                                                    onClick={resetFormFN}>
                                                <i className="fvp-left-chevron"/>Cancel
                                            </button>
                                            <div className="right-align">
                                                {(activeStepFN !== 0) && (
                                                    <button data-testid="step-prevFN"
                                                            className="fvp-button button-right-align" type="button"
                                                            onClick={handlePreviousClickFN}>
                                                        <i className="fvp-left-chevron"/> Previous
                                                    </button>
                                                )}
                                                {activeStepFN !== 3 && activeStepFN !== 1 && (
                                                    <button data-testid="step-nextFN"
                                                            className="fvp-button button-right-align" type="button"
                                                            onClick={() => handleNextClickFN(values)}>
                                                        Next <i className="fvp-right-chevron"/>
                                                    </button>
                                                )}
                                                {activeStepFN === 3 && (
                                                    <button data-testid="step-submitFN"
                                                            className="fvp-button button-right-align" type="button"
                                                            onClick={() => submitVehicleFootnotesConfigFN()}>
                                                        Submit
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Formik>
                <CFAdminNotification ref={childCompRefFN}/>
            </div>
    );
}

export default CFAdminVehicleFootnotesFN;